import { listItems } from '@/modules/inventory/services';
import { listLogisticsOrdersInRange } from '@/modules/logistics/services';
import {
  listReservationsForItem,
  listReservationsInRange,
} from '@/modules/reservations/services';
import type { Reservation } from '@/modules/reservations/types';
import type { CalendarEvent, CalendarEventFilters } from '@/modules/calendar/types';
import { filterReservationsForCalendar } from '@/modules/calendar/utils/eventFilters';
import { listMaintenanceTasksInRange } from '@/modules/maintenance/services/maintenanceRepository';
import {
  logisticsOrdersToCalendarEvents,
  maintenanceTasksToCalendarEvents,
  reservationsToCalendarEvents,
} from '@/modules/calendar/utils/eventAdapter';
import type { InventoryItem } from '@/modules/inventory/types';

export type FetchCalendarEventsParams = {
  rangeStart: string;
  rangeEnd: string;
  filters?: CalendarEventFilters;
};

export async function fetchReservationsForCalendar(
  params: FetchCalendarEventsParams,
): Promise<Reservation[]> {
  const { rangeStart, rangeEnd, filters } = params;

  if (filters?.itemId) {
    return listReservationsForItem(filters.itemId, rangeStart, rangeEnd);
  }

  const reservations = await listReservationsInRange(rangeStart, rangeEnd);
  if (!filters?.status && !filters?.categoryId) {
    return reservations;
  }

  const items = await listItems();
  const itemsById = new Map(items.map((i) => [i.id, i]));
  return filterReservationsForCalendar(reservations, filters, itemsById);
}

export async function fetchCalendarEvents(
  params: FetchCalendarEventsParams,
): Promise<CalendarEvent[]> {
  const { filters } = params;
  const showReservations = filters?.showReservations !== false;
  const showLogistics = filters?.showLogistics !== false;
  const showMaintenance = filters?.showMaintenance !== false;

  const [reservationEvents, logisticsEvents, maintenanceEvents] =
    await Promise.all([
      showReservations
        ? fetchReservationsForCalendar(params).then(reservationsToCalendarEvents)
        : Promise.resolve([]),
      showLogistics
        ? listLogisticsOrdersInRange(
            params.rangeStart,
            params.rangeEnd,
          ).then(logisticsOrdersToCalendarEvents)
        : Promise.resolve([]),
      showMaintenance
        ? listMaintenanceTasksInRange(
            params.rangeStart,
            params.rangeEnd,
          ).then(maintenanceTasksToCalendarEvents)
        : Promise.resolve([]),
    ]);

  let merged = [...reservationEvents, ...logisticsEvents, ...maintenanceEvents];

  if (filters?.itemId) {
    merged = merged.filter((e) =>
      e.relatedInventoryItems.includes(filters.itemId!),
    );
  }

  if (filters?.categoryId) {
    const items = await listItems();
    const itemsById = new Map(items.map((i) => [i.id, i]));
    merged = merged.filter((e) =>
      e.relatedInventoryItems.some(
        (itemId) => itemsById.get(itemId)?.categoryId === filters.categoryId,
      ),
    );
  }

  return merged.sort((a, b) => a.start.localeCompare(b.start));
}

export async function loadItemsLookup(): Promise<Map<string, InventoryItem>> {
  const items = await listItems();
  return new Map(items.map((i) => [i.id, i]));
}
